<template>
  <tr>
    <td>
      <div class="hstack gap-2">
        <AVSpinner
          v-if="isActive"
          size="sm"
          color="primary-30"
        />
        <div
          :class="trustee.connected ? 'bg-success pulse-success' : 'bg-danger'"
          class="circle-color"
          v-tooltip="
            trustee.connected
              ? t('backend.e2e.voting_round_reports.show.present')
              : t('backend.e2e.voting_round_reports.show.not_present')
          "
        >
      </div>
        {{ trustee.name }}
        <!-- Skips the trustee in every orchestrator of this result, the ones not started yet
             included - as opposed to the per-step skips, which only affect this orchestrator. -->
        <a
          v-if="canSkipForResult"
          role="button"
          :class="['link-danger', { 'pe-none': skippingForResult }]"
          :aria-busy="skippingForResult"
          v-tooltip="t('backend.e2e.voting_round_reports.show.skip_for_whole_result')"
          @click="skipForResult(trustee)"
        >
          <AVSpinner v-if="skippingForResult" size="sm" color="primary-30" />
          <AVIcon v-else icon="user-slash" />
        </a>
      </div>
    </td>
    <td>
      <div class="hstack justify-content-between me-5">
        <div class="hstack gap-2">
          <!-- The mixing chain runs in package position order, so the number says how many
               trustees are ahead of this one before it is their turn to mix. -->
          <span
            v-if="mixingPosition"
            class="text-muted font-monospace"
            v-tooltip="t('backend.e2e.voting_round_reports.show.mixing_position')"
          >
            #{{ mixingPosition }}
          </span>
          <span :class="`badge text-bg-${mixingBadge}`">
            {{ mixingLabel }}
          </span>
        </div>
        <div class="hstack gap-3">
          <a
            v-if="mixingRestartableStates.includes(mixingStatus) && trusteesUrl"
            role="button"
            :class="['link-dark', { 'pe-none': retryingMixing }]"
            :aria-busy="retryingMixing"
            v-tooltip="t('backend.actions.retry')"
            @click="retryMixing(trustee)"
          >
            <AVSpinner v-if="retryingMixing" size="sm" color="primary-30" />
            <AVIcon v-else icon="repeat" />
          </a>
          <a
            v-if="mixingRestartableStates.includes(mixingStatus) && canCancelMixing(trustee)"
            role="button"
            :class="['link-danger', { 'pe-none': cancellingMixing }]"
            :aria-busy="cancellingMixing"
            v-tooltip="t('backend.actions.skip')"
            @click="cancelMixing(trustee)"
          >
            <AVSpinner v-if="cancellingMixing" size="sm" color="primary-30" />
            <AVIcon v-else icon="times" />
          </a>
        </div>
      </div>
    </td>
    <td>
      <div class="hstack justify-content-between">
        <span :class="`badge text-bg-${decryptionBadge}`">
          {{ decryptionLabel }}
        </span>
        <div class="hstack gap-3" v-if="resultOrchestrator.status === 'decrypting'">
          <a
            v-if="decryptionRestartableStates.includes(decryptionStatus) && trusteesUrl"
            role="button"
            :class="['link-dark', { 'pe-none': retryingDecryption }]"
            :aria-busy="retryingDecryption"
            v-tooltip="t('backend.actions.retry')"
            @click="retryDecryption(trustee)"
          >
            <AVSpinner v-if="retryingDecryption" size="sm" color="primary-30" />
            <AVIcon v-else icon="repeat" />
          </a>
          <a
            v-if="decryptionRestartableStates.includes(decryptionStatus) && canCancelDecryption(trustee)"
            role="button"
            :class="['link-danger', { 'pe-none': cancellingDecryption }]"
            :aria-busy="cancellingDecryption"
            v-tooltip="t('backend.actions.skip')"
            @click="cancelDecryption(trustee)"
          >
            <AVSpinner v-if="cancellingDecryption" size="sm" color="primary-30" />
            <AVIcon v-else icon="times" />
          </a>
        </div>
      </div>
    </td>
  </tr>
</template>

<script setup lang="ts">
import apiClient from "@/entrypoints/frontend/apiClient";
import { useI18n } from "vue-i18n";
import { ref, computed } from "vue";
import {
  calculateTrusteeMixingStatus,
  calculateTrusteeDecryptionStatus,
  trusteeMixingPackage,
} from "@/entrypoints/shared/trustee_status_helpers";
import type {
  PropType,
  ElectaTrustee,
  E2E,
  WorkflowState,
} from "@/types";

const { t } = useI18n();

const props = defineProps({
  trustee: {
    type: Object as PropType<ElectaTrustee>,
    required: true,
  },
  resultOrchestrator: {
    type: Object as PropType<E2E.ResultOrchestrator>,
    required: true,
  },
  threshold: {
    type: Number,
    required: true,
  },
  isActive: {
    type: Boolean,
    default: false,
  },
});

const packageSuccessStatus = ref<Array<WorkflowState>>(["initial", "starting", "mixing", "decrypting", "validating", "finished"]);

const mixingRestartableStates = ref<Array<WorkflowState>>(["initial", "starting", "mixing", "validating", "failed"]);

const decryptionRestartableStates = ref<Array<WorkflowState>>(["initial", "decrypting", "validating", "failed"]);

const retryingMixing = ref<boolean>(false);

const retryingDecryption = ref<boolean>(false);

const cancellingMixing = ref<boolean>(false);

const cancellingDecryption = ref<boolean>(false);

const skippingForResult = ref<boolean>(false);

const mixingStatus = computed(() => calculateTrusteeMixingStatus(props.trustee, props.resultOrchestrator));

// Undefined for a trustee this orchestrator never made a mixing package for (not attending).
const mixingPosition = computed(() => trusteeMixingPackage(props.trustee, props.resultOrchestrator)?.position);

const decryptionStatus = computed(() => calculateTrusteeDecryptionStatus(props.trustee, props.resultOrchestrator));

const mixingLabel = computed(() => t(`js.result_progress.states.${mixingStatus.value}`));

const decryptionLabel = computed(() => t(`js.result_progress.states.${decryptionStatus.value}`));

const mixingBadge = computed(() => getStatusBadgeClass(mixingStatus.value));

const decryptionBadge = computed(() => getStatusBadgeClass(decryptionStatus.value));

const getStatusBadgeClass = (key: WorkflowState) => { 
  switch (key) {
    case "cancelled":
      return "info";
    case "failed":
      return "danger";
    case "finished":
    case "ready":
      return "success";
    default:
      return "secondary";      
  }
};

// As long as there is enough packages that isn't cancelled or failed we are allowed to cancel
const canCancelMixing = (trustee: ElectaTrustee) => {
  if (!trusteesUrl.value) return false;

  const mixingStatus = calculateTrusteeMixingStatus(trustee, props.resultOrchestrator);
  const remainingPackages = props.resultOrchestrator.mixingPackages?.filter((mixPackage) =>
    packageSuccessStatus.value.includes(mixPackage.status),
  ).length;

  if (packageSuccessStatus.value.includes(mixingStatus)) return remainingPackages > props.threshold;
  else return remainingPackages >= props.threshold;
};

// As long as there is enough packages that isn't cancelled or failed we are allowed to cancel
const canCancelDecryption = (trustee: ElectaTrustee) => {
  if (!trusteesUrl.value) return false;

  const decryptionStatus = calculateTrusteeDecryptionStatus(trustee, props.resultOrchestrator);
  const remainingPackages = props.resultOrchestrator.decryptionPackages?.filter((decPackage) =>
    packageSuccessStatus.value.includes(decPackage.status),
  ).length

  if (packageSuccessStatus.value.includes(decryptionStatus)) return remainingPackages > props.threshold;
  else return remainingPackages >= props.threshold;
};

// Mirrors the per-step skip buttons: skipping the trustee for the whole result is offered
// whenever there is a step left in this orchestrator that can spare them.
const canSkipForResult = computed(() =>
  (mixingRestartableStates.value.includes(mixingStatus.value) && canCancelMixing(props.trustee)) ||
  (decryptionRestartableStates.value.includes(decryptionStatus.value) && canCancelDecryption(props.trustee)));

const trusteesUrl = computed(() => {
  const url = new URL(window.location.href);
  const regex = /\/backend\/organisations\/(?<organisationId>\d+)\/elections\/(?<electionId>\d+)/;
  const match = url.pathname.match(regex);

  if (match) {
    const { organisationId, electionId } = match.groups;
    url.pathname = `/backend/organisations/${organisationId}/elections/${electionId}/e2e/trustees`;

    return url;
  }

  return null;
});

const cancelMixing = async (trustee: ElectaTrustee) => {
  const url = `${trusteesUrl.value}/cancel_mixing`;

  cancellingMixing.value = true;

  try {
    await apiClient.post(url, {
      id: trustee.id,
      mixing_package_id: props.resultOrchestrator.mixingPackages?.find(
        (mixPackage) => mixPackage.trusteeUUID === trustee.uuid,
      ).id,
    });
  } finally {
    cancellingMixing.value = false;
  }
};

const cancelDecryption = async (trustee: ElectaTrustee) => {
  const url = `${trusteesUrl.value}/cancel_decryption`;

  cancellingDecryption.value = true;

  try {
    await apiClient.post(url, {
      decryption_package_id: props.resultOrchestrator.decryptionPackages?.find(
        (decryptPackage) => decryptPackage.trusteeUUID === trustee.uuid,
      ).id,
      id: trustee.id,
    });
  } finally {
    cancellingDecryption.value = false;
  }
};

// The whole result, not just this orchestrator: the skip is recorded server side, so the
// orchestrators of this report that have not built their packages yet leave the trustee out too.
const skipForResult = async (trustee: ElectaTrustee) => {
  if (!confirm(t("backend.e2e.voting_round_reports.show.confirm_skip_for_whole_result", { name: trustee.name }))) return;

  const url = `${trusteesUrl.value}/skip_for_result`;

  skippingForResult.value = true;

  try {
    await apiClient.post(url, {
      id: trustee.id,
      voting_round_report_id: props.resultOrchestrator.votingRoundReportId,
    });
  } finally {
    skippingForResult.value = false;
  }
};

const retryDecryption = async (trustee: ElectaTrustee) => {
  const url = `${trusteesUrl.value}/retry_decryption`;

  retryingDecryption.value = true;

  try {
    await apiClient.post(url, {
      decryption_package_id: props.resultOrchestrator.decryptionPackages?.find(
        (decryptPackage) => decryptPackage.trusteeUUID === trustee.uuid,
      ).id,
      id: trustee.id,
    });
  } finally {
    retryingDecryption.value = false;
  }
};

const retryMixing = async (trustee: ElectaTrustee) => {
  const url = `${trusteesUrl.value}/retry_mixing`;

  retryingMixing.value = true;

  try {
    await apiClient.post(url, {
      id: trustee.id,
      mixing_package_id: props.resultOrchestrator.mixingPackages?.find(
        (mixPackage) => mixPackage.trusteeUUID === trustee.uuid,
      ).id,
    });
  } finally {
    retryingMixing.value = false;
  }
};
</script>
